import { loadImageFromSrc } from '@/utils/image';
// ==================== КОНСТАНТЫ ====================
/**
 * Максимальный размер Canvas в браузерах (обычно 16384px для одного измерения)
 * Используем консервативное значение для совместимости
 *
 * Экспортируем для использования в других модулях (например, для масштабирования размеров)
 */
export const MAX_CANVAS_SIZE = 16384;
/**
 * Максимальный pixelRatio для кеширования Konva.
 * Снижено с 32 до 2 для предотвращения переполнения памяти.
 * Адаптивный pixelRatio будет рассчитываться в зависимости от размера кеша.
 */
const MAX_CACHE_PIXEL_RATIO = 2;
/**
 * Максимальный размер кеша в пикселях (одно измерение).
 * Предотвращает создание слишком больших canvas кешей.
 */
const MAX_CACHE_SIZE_PIXELS = 4096;
/**
 * Минимальный zoom для кеширования.
 * Кешируем только при zoom < MIN_ZOOM_FOR_CACHE (когда видно много объектов).
 */
const MIN_ZOOM_FOR_CACHE = 1.0;
/**
 * Порог размера изображения для автоматического кэширования (>500px)
 */
const LARGE_IMAGE_THRESHOLD = 500;
/**
 * Максимальный размер кэша загруженных изображений (количество изображений)
 * Ограничивает потребление памяти для кэша HTMLImageElement
 */
const MAX_IMAGE_CACHE_SIZE = 200;
// ==================== ГЛОБАЛЬНЫЕ ПЕРЕМЕННЫЕ ====================
/**
 * Таймеры для отмены отложенного кэширования
 */
const cacheTimers = new WeakMap();
const cacheQueue = [];
let activeCacheTasks = 0;
const MAX_CONCURRENT_CACHE_TASKS_NORMAL = 2; // Максимум 2 одновременных операции кеширования в обычном режиме
const MAX_CONCURRENT_CACHE_TASKS_PAN_ZOOM = 1; // Максимум 1 одновременная операция кеширования во время pan/zoom для предотвращения падений FPS
let isProcessingQueue = false;
let isPanZoomActive = false; // Флаг активности pan/zoom
/**
 * Устанавливает флаг активности pan/zoom для оптимизации кеширования
 * @param active - true если pan/zoom активен, false если нет
 */
export const setPanZoomActive = (active) => {
    isPanZoomActive = active;
};
/**
 * Получает максимальное количество одновременных операций кеширования
 */
const getMaxConcurrentTasks = () => {
    return isPanZoomActive ? MAX_CONCURRENT_CACHE_TASKS_PAN_ZOOM : MAX_CONCURRENT_CACHE_TASKS_NORMAL;
};
/**
 * Глобальный реестр закэшированных изображений.
 *
 * Назначение: предотвращает дублирование кэша для одинаковых изображений с одинаковыми параметрами.
 * Это критично для экономии памяти, так как кэш Konva занимает много места.
 *
 * Ключ: комбинация URL изображения и параметров кэширования (zoom, размеры).
 * Значение: количество узлов Konva, использующих этот кэш.
 *
 * Пример: если на доске 10 одинаковых изображений с одним zoom и размером,
 * то только первое будет закэшировано, остальные 9 будут использовать тот же кэш.
 */
const cachedImagesRegistry = new Map();
// ==================== УТИЛИТЫ ДЛЯ ПРОВЕРКИ КЭШИРОВАНИЯ ====================
/**
 * Проверяет, можно ли безопасно кэшировать изображение с учетом ограничений Canvas
 */
export const canCacheImage = (img, zoom, pixelRatio = 1, targetWidth, targetHeight) => {
    const baseWidth = Math.max(1, targetWidth !== null && targetWidth !== void 0 ? targetWidth : img.naturalWidth);
    const baseHeight = Math.max(1, targetHeight !== null && targetHeight !== void 0 ? targetHeight : img.naturalHeight);
    const cachedWidth = baseWidth * pixelRatio;
    const cachedHeight = baseHeight * pixelRatio;
    return cachedWidth <= MAX_CANVAS_SIZE && cachedHeight <= MAX_CANVAS_SIZE;
};
/**
 * Определяет, нужно ли кэшировать изображение на основе его размера и зума.
 * УСТАРЕВШЕЕ: используйте shouldCacheImage вместо этой функции.
 * @deprecated Используйте shouldCacheImage с проверкой видимости
 */
export const shouldCacheBySize = (img, zoom) => {
    return (img.naturalWidth > LARGE_IMAGE_THRESHOLD ||
        img.naturalHeight > LARGE_IMAGE_THRESHOLD ||
        zoom < MIN_ZOOM_FOR_CACHE);
};
/**
 * Определяет, нужно ли кэшировать изображение на основе zoom, видимости и размера.
 * Следует рекомендациям разработчиков Konva:
 * - Кешировать только при малом zoom (когда видно много объектов)
 * - Кешировать только видимые элементы
 * - Кешировать только достаточно большие изображения
 *
 * @param img - HTMLImageElement изображения
 * @param zoom - текущий zoom
 * @param isVisible - виден ли элемент на экране
 * @param width - ширина элемента
 * @param height - высота элемента
 * @returns true, если нужно кешировать
 */
export const shouldCacheImage = (img, zoom, isVisible, width, height) => {
    // Не кешируем, если элемент не виден
    if (!isVisible) {
        return false;
    }
    // Не кешируем при большом zoom (zoom > 1.0) - следуем рекомендациям Konva
    if (zoom > MIN_ZOOM_FOR_CACHE) {
        return false;
    }
    // Не кешируем слишком маленькие изображения
    if (img.naturalWidth < LARGE_IMAGE_THRESHOLD && img.naturalHeight < LARGE_IMAGE_THRESHOLD) {
        return false;
    }
    // Проверяем, что размер кеша не превысит лимит
    const maxDimension = Math.max(width, height);
    if (maxDimension > MAX_CACHE_SIZE_PIXELS) {
        return false;
    }
    return true;
};
/**
 * Вычисляет оптимальный pixelRatio для кеширования на основе размера и zoom.
 * Следует рекомендациям разработчиков Konva: использовать низкий pixelRatio для больших кешей.
 *
 * Логика:
 * - Для больших кешей (> 2000px) → pixelRatio = 0.5-1.0
 * - Для средних кешей (1000-2000px) → pixelRatio = 1.0-1.5
 * - Для маленьких кешей (< 1000px) → pixelRatio = 1.5-2.0
 * - Учитывает zoom: при малом zoom можно использовать более высокий pixelRatio
 *
 * @param baseWidth - базовая ширина элемента
 * @param baseHeight - базовая высота элемента
 * @param zoom - текущий zoom
 * @param imgNaturalWidth - натуральная ширина изображения
 * @param imgNaturalHeight - натуральная высота изображения
 * @returns оптимальный pixelRatio (от 0.5 до MAX_CACHE_PIXEL_RATIO)
 */
export const calculateOptimalPixelRatio = (baseWidth, baseHeight, zoom, imgNaturalWidth, imgNaturalHeight) => {
    const maxDimension = Math.max(baseWidth, baseHeight);
    // Базовый pixelRatio в зависимости от размера кеша
    let basePixelRatio;
    if (maxDimension > 2000) {
        // Большие кеши - используем низкий pixelRatio
        basePixelRatio = 0.5;
    }
    else if (maxDimension > 1000) {
        // Средние кеши
        basePixelRatio = 1.0;
    }
    else {
        // Маленькие кеши - можно использовать более высокий pixelRatio
        basePixelRatio = 1.5;
    }
    // Учитываем zoom: при малом zoom можно использовать более высокий pixelRatio
    // При zoom < 0.5 увеличиваем pixelRatio, но не более чем в 1.5 раза
    let zoomMultiplier = 1.0;
    if (zoom < 0.5) {
        zoomMultiplier = 1.0 + (0.5 - zoom) * 0.5; // От 1.0 до 1.25
    }
    let pixelRatio = basePixelRatio * zoomMultiplier;
    // Учитываем соотношение исходного изображения и размера элемента
    // Если изображение намного больше элемента, можно использовать более высокий pixelRatio
    const scaleRatio = Math.min(imgNaturalWidth / baseWidth, imgNaturalHeight / baseHeight);
    if (scaleRatio > 2) {
        // Изображение в 2+ раза больше элемента - можно немного увеличить pixelRatio
        pixelRatio = Math.min(pixelRatio * 1.2, MAX_CACHE_PIXEL_RATIO);
    }
    // Ограничиваем максимальным значением
    pixelRatio = Math.min(pixelRatio, MAX_CACHE_PIXEL_RATIO);
    // Ограничиваем минимальным значением (0.5 для качества)
    pixelRatio = Math.max(pixelRatio, 0.5);
    // Проверяем, что кеш поместится в Canvas
    const cachedWidth = baseWidth * pixelRatio;
    const cachedHeight = baseHeight * pixelRatio;
    if (cachedWidth > MAX_CANVAS_SIZE || cachedHeight > MAX_CANVAS_SIZE) {
        // Уменьшаем pixelRatio до безопасного значения
        const maxPixelRatioX = Math.floor(MAX_CANVAS_SIZE / baseWidth);
        const maxPixelRatioY = Math.floor(MAX_CANVAS_SIZE / baseHeight);
        pixelRatio = Math.min(pixelRatio, maxPixelRatioX, maxPixelRatioY, 1.0);
    }
    // Проверяем максимальный размер кеша
    if (cachedWidth > MAX_CACHE_SIZE_PIXELS || cachedHeight > MAX_CACHE_SIZE_PIXELS) {
        const maxPixelRatioX = Math.floor(MAX_CACHE_SIZE_PIXELS / baseWidth);
        const maxPixelRatioY = Math.floor(MAX_CACHE_SIZE_PIXELS / baseHeight);
        pixelRatio = Math.min(pixelRatio, maxPixelRatioX, maxPixelRatioY);
    }
    return Math.max(0.5, pixelRatio);
};
// ==================== РЕЕСТР КЭШИРОВАННЫХ ИЗОБРАЖЕНИЙ ====================
/**
 * Генерирует уникальный ключ для реестра кэшированных изображений.
 *
 * Ключ включает:
 * - URL изображения (src или currentSrc)
 * - Zoom (округленный до 2 знаков для группировки похожих значений)
 * - Ширина и высота целевого размера
 *
 * @param img - HTMLImageElement изображения
 * @param zoom - текущий zoom
 * @param targetWidth - целевая ширина (опционально)
 * @param targetHeight - целевая высота (опционально)
 * @returns уникальный ключ для реестра
 */
const getCacheKey = (img, zoom, targetWidth, targetHeight) => {
    const url = img.src || img.currentSrc || '';
    const width = targetWidth !== null && targetWidth !== void 0 ? targetWidth : img.naturalWidth;
    const height = targetHeight !== null && targetHeight !== void 0 ? targetHeight : img.naturalHeight;
    // Округляем zoom до 2 знаков для группировки похожих значений (например, 0.33 и 0.34)
    const zoomRounded = Math.round(zoom * 100) / 100;
    return `${url}|${zoomRounded}|${width}|${height}`;
};
/**
 * Проверяет, закэшировано ли уже изображение с такими же параметрами.
 *
 * Используется перед кэшированием, чтобы избежать дублирования.
 *
 * @param img - HTMLImageElement изображения
 * @param zoom - текущий zoom
 * @param targetWidth - целевая ширина (опционально)
 * @param targetHeight - целевая высота (опционально)
 * @returns true, если изображение уже закэшировано
 */
export const isImageCached = (img, zoom, targetWidth, targetHeight) => {
    var _a;
    const key = getCacheKey(img, zoom, targetWidth, targetHeight);
    return cachedImagesRegistry.has(key) && ((_a = cachedImagesRegistry.get(key)) !== null && _a !== void 0 ? _a : 0) > 0;
};
/**
 * Регистрирует кэширование изображения в глобальном реестре.
 *
 * Увеличивает счетчик использования для данного ключа.
 * Вызывается после успешного кэширования узла Konva.
 *
 * @param img - HTMLImageElement изображения
 * @param zoom - текущий zoom
 * @param targetWidth - целевая ширина (опционально)
 * @param targetHeight - целевая высота (опционально)
 */
export const registerImageCache = (img, zoom, targetWidth, targetHeight) => {
    var _a;
    const key = getCacheKey(img, zoom, targetWidth, targetHeight);
    const count = (_a = cachedImagesRegistry.get(key)) !== null && _a !== void 0 ? _a : 0;
    cachedImagesRegistry.set(key, count + 1);
};
/**
 * Отменяет регистрацию кэширования изображения в глобальном реестре.
 *
 * Уменьшает счетчик использования для данного ключа.
 * Если счетчик достигает 0, ключ удаляется из реестра.
 * Вызывается при очистке кэша узла Konva.
 *
 * @param img - HTMLImageElement изображения
 * @param zoom - текущий zoom
 * @param targetWidth - целевая ширина (опционально)
 * @param targetHeight - целевая высота (опционально)
 */
export const unregisterImageCache = (img, zoom, targetWidth, targetHeight) => {
    var _a;
    const key = getCacheKey(img, zoom, targetWidth, targetHeight);
    const count = (_a = cachedImagesRegistry.get(key)) !== null && _a !== void 0 ? _a : 0;
    if (count > 1) {
        // Есть другие узлы, использующие этот кэш - просто уменьшаем счетчик
        cachedImagesRegistry.set(key, count - 1);
    }
    else {
        // Это был последний узел - удаляем ключ из реестра
        cachedImagesRegistry.delete(key);
    }
};
// ==================== УТИЛИТЫ ДЛЯ РАБОТЫ С КЭШЕМ KONVA ====================
/**
 * Отменяет запланированное кэширование для указанного узла Konva
 */
export const cancelCacheTimer = (konvaNode) => {
    if (!konvaNode)
        return;
    const timer = cacheTimers.get(konvaNode);
    if (timer) {
        if ('cancelIdleCallback' in window) {
            window.cancelIdleCallback(timer);
        }
        else {
            clearTimeout(timer);
        }
        cacheTimers.delete(konvaNode);
    }
};
/**
 * Очищает кэш Konva узла и отменяет регистрацию в глобальном реестре
 * @param konvaNode - узел Konva, кэш которого нужно очистить
 * @param cacheParamsRef - ref с параметрами кэширования для отмены регистрации
 */
export const clearCacheWithUnregister = (konvaNode, cacheParamsRef) => {
    if (!konvaNode)
        return;
    try {
        // Отменяем регистрацию кэша в глобальном реестре перед очисткой
        if (cacheParamsRef.current) {
            unregisterImageCache(cacheParamsRef.current.img, cacheParamsRef.current.zoom, cacheParamsRef.current.width, cacheParamsRef.current.height);
            cacheParamsRef.current = null;
        }
        // Очищаем кэш узла Konva
        if (konvaNode.clearCache) {
            konvaNode.clearCache();
        }
    }
    catch (e) {
        // Игнорируем ошибки очистки кэша
    }
};
/**
 * Полная очистка: отменяет таймер, отменяет регистрацию и очищает кэш
 * Используется при размонтировании компонента или изменении параметров
 */
export const cleanupCache = (konvaNode, cacheParamsRef) => {
    if (!konvaNode)
        return;
    // Отменяем запланированное кэширование
    cancelCacheTimer(konvaNode);
    // Очищаем кэш и отменяем регистрацию
    clearCacheWithUnregister(konvaNode, cacheParamsRef);
};
/**
 * Проверяет, нужно ли пересоздать кэш на основе изменения параметров.
 *
 * @param isCached - флаг, указывающий, что кэш создан
 * @param cacheParams - текущие параметры кэширования
 * @param img - новое изображение
 * @param zoom - новый zoom
 * @param width - новая ширина
 * @param height - новая высота
 * @returns true, если нужно пересоздать кэш
 */
export const shouldRecache = (isCached, cacheParams, img, zoom, width, height) => {
    return (!isCached ||
        !cacheParams ||
        cacheParams.img !== img ||
        cacheParams.zoom !== zoom ||
        cacheParams.width !== width ||
        cacheParams.height !== height);
};
/**
 * Создает параметры кэширования для сохранения в ref.
 *
 * @param img - изображение
 * @param zoom - zoom
 * @param width - ширина
 * @param height - высота
 * @returns параметры кэширования
 */
export const createCacheParams = (img, zoom, width, height) => {
    return { img, zoom, width, height };
};
/**
 * Очищает кэш и сбрасывает флаги состояния кэширования.
 *
 * @param konvaNode - узел Konva
 * @param cacheParamsRef - ref с параметрами кэширования
 * @param isCachedRef - ref с флагом кэширования
 * @param cacheFailedRef - ref с флагом ошибки кэширования (опционально)
 */
export const clearCacheAndResetFlags = (konvaNode, cacheParamsRef, isCachedRef, cacheFailedRef) => {
    if (!konvaNode)
        return;
    clearCacheWithUnregister(konvaNode, cacheParamsRef);
    isCachedRef.current = false;
    if (cacheFailedRef) {
        cacheFailedRef.current = false;
    }
};
/**
 * Обеспечивает наличие кэша для изображения с фильтрами.
 * Пытается кэшировать синхронно, при неудаче пробует асинхронное кэширование.
 *
 * @param konvaNode - узел Konva для кэширования
 * @param img - изображение
 * @param zoom - zoom
 * @param width - ширина
 * @param height - высота
 * @param cacheParamsRef - ref для сохранения параметров кэширования
 * @param isCachedRef - ref для флага кэширования
 * @param cacheFailedRef - ref для флага ошибки кэширования
 * @returns true, если кэш был создан или уже существует
 */
export const ensureCacheForFilters = (konvaNode, img, zoom, width, height, cacheParamsRef, isCachedRef, cacheFailedRef) => {
    if (isCachedRef.current) {
        return true; // Кэш уже создан
    }
    if (cacheFailedRef.current) {
        return false; // Кэширование уже не удалось
    }
    // Пытаемся кэшировать синхронно для немедленного применения фильтров
    const cached = cacheImageSyncForFilters(konvaNode, img, zoom, width, height);
    if (cached) {
        cacheParamsRef.current = createCacheParams(img, zoom, width, height);
        isCachedRef.current = true;
        return true;
    }
    // Если синхронное кэширование не удалось, пробуем асинхронное
    if (canCacheImage(img, zoom)) {
        cacheParamsRef.current = createCacheParams(img, zoom, width, height);
        // forceCache=true для принудительного кэширования, skipRegistryCheck=true для фильтров
        // Для фильтров всегда считаем элемент видимым (isVisible=true)
        cacheImageAsync(konvaNode, img, zoom, true, true, width, height, true);
        isCachedRef.current = true;
        return true;
    }
    // Изображение слишком большое для кэширования
    console.warn(`[ensureCacheForFilters] Image too large to cache: ${img.naturalWidth}x${img.naturalHeight}px. Filters may not work correctly.`);
    cacheFailedRef.current = true;
    return false;
};
/**
 * Обрабатывает задачу кеширования из очереди
 */
const processCacheTask = (task) => {
    var _a;
    try {
        const { konvaNode, img, zoom, isVisible, forceCache, targetWidth, targetHeight, skipRegistryCheck, } = task;
        const baseWidth = Math.max(1, targetWidth !== null && targetWidth !== void 0 ? targetWidth : konvaNode.width());
        const baseHeight = Math.max(1, targetHeight !== null && targetHeight !== void 0 ? targetHeight : konvaNode.height());
        // Проверяем, нужно ли кешировать (если не принудительное кеширование для фильтров)
        if (!forceCache && !shouldCacheImage(img, zoom, isVisible, baseWidth, baseHeight)) {
            return;
        }
        // Для фильтров Konva нужно всегда кэшировать, даже если изображение уже в реестре
        // Фильтры работают только с кэшированными узлами
        if (!skipRegistryCheck && isImageCached(img, zoom, baseWidth, baseHeight)) {
            // Изображение уже закэшировано, не кэшируем повторно
            return;
        }
        // Используем адаптивный pixelRatio для оптимизации памяти
        let pixelRatio;
        if (forceCache) {
            // Для фильтров используем минимальный pixelRatio для экономии памяти
            pixelRatio = Math.min(1.0, calculateOptimalPixelRatio(baseWidth, baseHeight, zoom, img.naturalWidth, img.naturalHeight));
        }
        else {
            // Для обычных изображений используем оптимальный pixelRatio
            pixelRatio = calculateOptimalPixelRatio(baseWidth, baseHeight, zoom, img.naturalWidth, img.naturalHeight);
        }
        // Финальная проверка безопасности
        if (!canCacheImage(img, zoom, pixelRatio, baseWidth, baseHeight)) {
            console.warn(`Image too large to cache: ${img.naturalWidth}x${img.naturalHeight}px. Canvas limit: ${MAX_CANVAS_SIZE}px`);
            return;
        }
        // Очищаем предыдущий кэш перед созданием нового
        if (konvaNode.getLayer()) {
            try {
                konvaNode.clearCache();
            }
            catch (e) {
                // Игнорируем ошибки очистки
            }
        }
        konvaNode.cache({ pixelRatio });
        (_a = konvaNode.getLayer()) === null || _a === void 0 ? void 0 : _a.batchDraw();
        // Регистрируем кэширование изображения только если не пропускаем проверку реестра
        // (для фильтров каждый узел должен иметь свой кэш)
        if (!skipRegistryCheck) {
            registerImageCache(img, zoom, baseWidth, baseHeight);
        }
    }
    catch (error) {
        console.warn('Failed to cache image:', error);
    }
};
/**
 * Обрабатывает очередь кеширования
 */
const processCacheQueue = () => {
    const maxConcurrent = getMaxConcurrentTasks();
    if (isProcessingQueue || activeCacheTasks >= maxConcurrent) {
        return;
    }
    // Сортируем очередь: сначала высокий приоритет, потом низкий
    cacheQueue.sort((a, b) => {
        if (a.priority === 'high' && b.priority === 'low')
            return -1;
        if (a.priority === 'low' && b.priority === 'high')
            return 1;
        return 0;
    });
    // Обрабатываем задачи пока не достигнем лимита параллелизма
    while (activeCacheTasks < maxConcurrent && cacheQueue.length > 0) {
        const task = cacheQueue.shift();
        if (!task)
            break;
        activeCacheTasks++;
        isProcessingQueue = true;
        // Используем requestIdleCallback для неблокирующего кеширования
        const scheduleTask = () => {
            processCacheTask(task);
            activeCacheTasks--;
            isProcessingQueue = false;
            // Продолжаем обработку очереди
            if (cacheQueue.length > 0) {
                if ('requestIdleCallback' in window) {
                    const win = window;
                    win.requestIdleCallback(processCacheQueue, { timeout: 50 });
                }
                else {
                    setTimeout(processCacheQueue, 10);
                }
            }
        };
        const timeout = task.priority === 'high' ? 100 : 2000;
        if ('requestIdleCallback' in window) {
            const win = window;
            const timerId = win.requestIdleCallback(scheduleTask, { timeout });
            cacheTimers.set(task.konvaNode, timerId);
        }
        else {
            const delay = task.priority === 'high' ? 10 : 50;
            const timerId = setTimeout(scheduleTask, delay);
            cacheTimers.set(task.konvaNode, timerId);
        }
    }
    isProcessingQueue = false;
};
/**
 * Асинхронно кэширует изображение Konva в фоне, не блокируя основной поток.
 * Использует глобальную очередь с ограничением параллелизма для предотвращения блокировки UI.
 * Автоматически обрабатывает ограничения Canvas браузера.
 * Отменяет предыдущие запросы кэширования для того же узла.
 *
 * Следует рекомендациям разработчиков Konva:
 * - Кеширует только видимые элементы при малом zoom
 * - Использует адаптивный pixelRatio для оптимизации памяти
 * - Ограничивает параллелизм для предотвращения блокировки UI
 *
 * @param konvaNode - узел Konva для кэширования
 * @param img - HTMLImageElement изображения
 * @param zoom - текущий zoom
 * @param isVisible - виден ли элемент на экране
 * @param forceCache - если true, кэширует даже для маленьких изображений (для фильтров)
 * @param targetWidth - целевая ширина (опционально)
 * @param targetHeight - целевая высота (опционально)
 * @param skipRegistryCheck - если true, кэширует всегда (для фильтров Konva, которые требуют кэш на каждом узле)
 * @param priority - приоритет кэширования: 'high' (100мс) или 'low' (2000мс), по умолчанию 'low'
 */
export const cacheImageAsync = (konvaNode, img, zoom, isVisible = true, forceCache = false, targetWidth, targetHeight, skipRegistryCheck = false, priority = 'low') => {
    // Отменяем предыдущий запрос кэширования для этого узла
    cancelCacheTimer(konvaNode);
    // Удаляем старую задачу из очереди, если она есть
    const existingTaskIndex = cacheQueue.findIndex((task) => task.konvaNode === konvaNode);
    if (existingTaskIndex !== -1) {
        cacheQueue.splice(existingTaskIndex, 1);
    }
    // Добавляем новую задачу в очередь
    cacheQueue.push({
        konvaNode,
        img,
        zoom,
        isVisible,
        forceCache,
        targetWidth,
        targetHeight,
        skipRegistryCheck,
        priority,
    });
    // Запускаем обработку очереди
    const maxConcurrent = getMaxConcurrentTasks();
    if (!isProcessingQueue && activeCacheTasks < maxConcurrent) {
        if ('requestIdleCallback' in window) {
            const win = window;
            win.requestIdleCallback(processCacheQueue, {
                timeout: priority === 'high' ? 100 : 2000,
            });
        }
        else {
            const delay = priority === 'high' ? 10 : 50;
            setTimeout(processCacheQueue, delay);
        }
    }
};
/**
 * Синхронно кэширует маленькое изображение Konva.
 * Используется для изображений, которые не требуют асинхронного кэширования.
 * Проверяет реестр перед кэшированием, чтобы избежать дублирования.
 *
 * @param konvaNode - узел Konva для кэширования
 * @param img - HTMLImageElement изображения
 * @param zoom - текущий zoom
 * @param targetWidth - целевая ширина (опционально)
 * @param targetHeight - целевая высота (опционально)
 * @param skipRegistryCheck - если true, кэширует всегда (для фильтров Konva, которые требуют кэш на каждом узле)
 * @returns true, если кэширование выполнено успешно, false если изображение уже закэшировано (и skipRegistryCheck=false)
 */
export const cacheImageSync = (konvaNode, img, zoom, targetWidth, targetHeight, skipRegistryCheck = false) => {
    const baseWidth = Math.max(1, targetWidth !== null && targetWidth !== void 0 ? targetWidth : konvaNode.width());
    const baseHeight = Math.max(1, targetHeight !== null && targetHeight !== void 0 ? targetHeight : konvaNode.height());
    // Для фильтров Konva нужно всегда кэшировать, даже если изображение уже в реестре
    // Фильтры работают только с кэшированными узлами
    if (!skipRegistryCheck && isImageCached(img, zoom, baseWidth, baseHeight)) {
        return false; // Изображение уже закэшировано
    }
    try {
        konvaNode.cache();
        // Регистрируем только если не пропускаем проверку реестра
        // (для фильтров каждый узел должен иметь свой кэш)
        if (!skipRegistryCheck) {
            registerImageCache(img, zoom, baseWidth, baseHeight);
        }
        return true; // Кэширование выполнено успешно
    }
    catch (error) {
        console.warn('[cacheImageSync] Failed to cache image:', error);
        throw error;
    }
};
/**
 * Синхронно кэширует изображение Konva для фильтров.
 * Используется для изображений с фильтрами, которые требуют кэш на каждом узле.
 * Всегда кэширует синхронно, независимо от размера, и обновляет слой.
 *
 * Особенности:
 * - Кэширует синхронно для немедленного применения фильтров
 * - Не регистрирует в реестре (каждый узел с фильтрами должен иметь свой кэш)
 * - Автоматически обновляет слой после кэширования
 *
 * @param konvaNode - узел Konva для кэширования
 * @param _img - HTMLImageElement изображения (не используется, но нужен для совместимости)
 * @param _zoom - текущий zoom (не используется, но нужен для совместимости)
 * @param _targetWidth - целевая ширина (опционально, не используется)
 * @param _targetHeight - целевая высота (опционально, не используется)
 * @returns true, если кэширование выполнено успешно, false в случае ошибки
 */
export const cacheImageSyncForFilters = (konvaNode, _img, _zoom, _targetWidth, _targetHeight) => {
    try {
        // Для фильтров всегда кэшируем синхронно, независимо от размера
        // Это критично, так как фильтры не работают без кэша
        konvaNode.cache();
        // Принудительно обновляем слой после кэширования
        const layer = konvaNode.getLayer();
        if (layer) {
            layer.batchDraw();
        }
        return true; // Кэширование выполнено успешно
    }
    catch (error) {
        console.warn('[cacheImageSyncForFilters] Failed to cache image for filters:', error);
        return false; // Возвращаем false вместо throw для удобства обработки
    }
};
// ==================== КЭШ ЗАГРУЗКИ ИЗОБРАЖЕНИЙ ====================
/**
 * Кэш промисов загрузки изображений.
 * Предотвращает повторную загрузку одного и того же изображения.
 */
const imageLoadPromises = {};
/**
 * Кэш успешно загруженных изображений.
 * Хранит HTMLImageElement для быстрого доступа без повторной загрузки.
 */
const imageCache = {};
/**
 * Очищает старые записи из кэша загруженных изображений, если превышен лимит.
 * Использует стратегию FIFO (First In, First Out) для удаления самых старых записей.
 */
const cleanupImageCache = () => {
    const cacheKeys = Object.keys(imageCache);
    if (cacheKeys.length > MAX_IMAGE_CACHE_SIZE) {
        // Удаляем самые старые записи (FIFO)
        const keysToRemove = cacheKeys.slice(0, cacheKeys.length - MAX_IMAGE_CACHE_SIZE);
        keysToRemove.forEach((key) => {
            delete imageCache[key];
            // Также очищаем промисы для этих изображений
            delete imageLoadPromises[key];
        });
    }
};
/**
 * Загружает изображение с кэшированием промисов и результатов.
 *
 * Особенности:
 * - Кэширует промисы загрузки, чтобы избежать параллельных запросов одного изображения
 * - Кэширует успешно загруженные изображения для быстрого доступа
 * - Автоматически очищает старые записи при превышении лимита
 * - Не добавляет в кэш сломанные изображения
 *
 * @param url - URL изображения для загрузки
 * @returns Promise с HTMLImageElement или undefined в случае ошибки
 */
export function getCachedImage(url) {
    if (!url)
        return Promise.resolve(undefined);
    // Проверяем кэш загруженных изображений
    const cachedImg = imageCache[url];
    if ((cachedImg === null || cachedImg === void 0 ? void 0 : cachedImg.complete) && cachedImg.naturalWidth > 0 && cachedImg.naturalHeight > 0) {
        return Promise.resolve(cachedImg);
    }
    if (cachedImg) {
        // Изображение в кэше, но оно сломанное - удаляем
        delete imageCache[url];
    }
    // Проверяем кэш промисов загрузки
    if (url in imageLoadPromises) {
        return imageLoadPromises[url];
    }
    // Создаем новый промис загрузки
    const loadPromise = loadImageFromSrc(url, 'anonymous')
        .then((img) => {
        if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
            // Очищаем кэш перед добавлением нового изображения (если превышен лимит)
            cleanupImageCache();
            imageCache[url] = img;
            return img;
        }
        throw new Error('Image loaded but invalid');
    })
        .catch((error) => {
        delete imageLoadPromises[url];
        console.warn(`Failed to load image: ${url}`, error);
        return undefined;
    });
    imageLoadPromises[url] = loadPromise;
    return loadPromise;
}
/**
 * Приоритизирует кэширование видимых изображений на Stage.
 * Находит все видимые Image узлы в расширенной буферной зоне viewport и кэширует их с высоким приоритетом (100мс).
 * Используется при начале pan/zoom для предотвращения фризов.
 *
 * @param stage - Konva Stage
 * @param zoom - текущий zoom
 * @param viewportWidth - ширина viewport
 * @param viewportHeight - высота viewport
 * @param maxElements - максимальное количество элементов для кэширования за один раз (по умолчанию 30)
 */
export const prioritizeCacheForVisibleElements = (stage, zoom, viewportWidth, viewportHeight, maxElements = 30) => {
    if (!stage)
        return;
    // Получаем позицию Stage для вычисления viewport bounds
    const stagePosition = { x: stage.x(), y: stage.y() };
    // Вычисляем расширенную буферную зону для кэширования (2/3 ширины экрана, как в isElementInViewportForCache)
    const cacheBuffer = (viewportWidth * 2) / 3;
    // Вычисляем область видимости в мировых координатах
    const viewportLeft = -stagePosition.x / zoom;
    const viewportTop = -stagePosition.y / zoom;
    const viewportRight = viewportLeft + viewportWidth / zoom;
    const viewportBottom = viewportTop + viewportHeight / zoom;
    // Расширяем область видимости для буфера кэширования
    const bufferInWorld = cacheBuffer / zoom;
    const searchLeft = viewportLeft - bufferInWorld;
    const searchTop = viewportTop - bufferInWorld;
    const searchRight = viewportRight + bufferInWorld;
    const searchBottom = viewportBottom + bufferInWorld;
    // Находим все Image узлы на Stage
    const imageNodes = stage.find('Image');
    // Фильтруем только видимые узлы в расширенной зоне viewport, которые еще не закэшированы
    const visibleUncachedImages = [];
    for (const node of imageNodes) {
        // Пропускаем невидимые узлы
        if (!node.visible())
            continue;
        // Получаем HTMLImageElement из узла
        const img = node.image();
        if (!img || !(img instanceof HTMLImageElement))
            continue;
        // Проверяем, закэширован ли уже узел
        if (node.isCached())
            continue;
        // Проверяем, находится ли узел в расширенной зоне viewport
        const nodePos = node.position();
        const nodeX = nodePos.x;
        const nodeY = nodePos.y;
        const nodeWidth = node.width();
        const nodeHeight = node.height();
        const nodeRight = nodeX + nodeWidth;
        const nodeBottom = nodeY + nodeHeight;
        // Проверяем пересечение с расширенной областью видимости
        const intersectsHorizontally = nodeRight >= searchLeft && nodeX <= searchRight;
        const intersectsVertically = nodeBottom >= searchTop && nodeY <= searchBottom;
        if (!intersectsHorizontally || !intersectsVertically)
            continue;
        // Проверяем, нужно ли кэшировать это изображение
        if (!shouldCacheImage(img, zoom, true, nodeWidth, nodeHeight))
            continue;
        // После всех проверок img гарантированно является HTMLImageElement
        visibleUncachedImages.push({ node, img: img });
        // Ограничиваем количество элементов для предотвращения перегрузки
        if (visibleUncachedImages.length >= maxElements)
            break;
    }
    // Кэшируем найденные изображения с высоким приоритетом
    for (const { node, img } of visibleUncachedImages) {
        const width = node.width();
        const height = node.height();
        cacheImageAsync(node, img, zoom, true, false, width, height, false, 'high');
    }
};
